"use client";

import React, { useEffect, useState } from "react";
import { Search, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-5 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="px-3 py-2 text-xs font-sans font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary data-[active=true]:text-primary">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-32 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-2 mt-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block py-3 px-2 text-base font-sans font-semibold uppercase tracking-wider text-foreground hover:text-primary transition-colors whitespace-nowrap">
          {item.label}
        </Link>)}
    </div>;
}
function SearchOverlay() {
  const [searchQuery, setSearchQuery] = useState("");
  return <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="text-foreground hover:text-primary">
          <Search className="size-5" />
          <span className="sr-only">Buscar noticias</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 mt-2 bg-background border-border shadow-lg rounded-md" align="end">
        <div className="space-y-2">
          <h4 className="font-default font-bold text-sm text-foreground">Buscar en 239 Ahora</h4>
          <ProductsSearch value={searchQuery} onChange={setSearchQuery} placeholder="Explorar titulares..." className="w-full font-sans" />
        </div>
      </PopoverContent>
    </Popover>;
}
export default function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="253632" id="cabecera-principal" className="sticky top-0 z-50 w-full bg-background text-foreground">
      <header className={cn("w-full transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent")}>
        <div className="container mx-auto h-20 flex items-center justify-between !text-[rgba(255,255,255,1)] !opacity-[1] ![padding:1px_16px_0px_16px] !bg-[rgba(27,36,51,1)]">
          <div className="flex items-center">
            <Link to="/" className="flex !bg-transparent !text-2xl ![padding:0px_4px_0px_4px] !items-start ![margin:1px_0px_1px_0px] !font-mono !not-italic !font-semibold text-primary-foreground">239AHORA<span className="h-7 w-auto" data-wvc-role="logo" data-ast-id="f:/src/reusable_sections/Header.tsx|sig:ysp5g4" data-ast-start="5663" data-ast-end="6033" data-array-property-names="imgUrl" data-attr-property-mappings="className:className" contenteditable="false" style={{
              display: 'inline-block',
              maxHeight: '26px'
            }}></span></Link>
          </div>
          
          <div className="flex items-center gap-2 md:gap-6">
            <MenuProvider menu_id="53">
              <DesktopNav />
              
              <div className="flex items-center gap-2">
                <SearchOverlay />
                
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="default" size="icon" className="md:hidden hover:text-primary text-primary-foreground bg-primary" aria-label="Abrir menú">
                      <Menu className="size-6" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                    <SheetTitle className="sr-only">Menú de Navegación</SheetTitle>
                    <MobileNav />
                  </SheetContent>
                </Sheet>
              </div>
            </MenuProvider>
          </div>
        </div>
      </header>
    </section>;
}